/**
 * ExecutionTimeline — Phase 2a (Operational Observation)
 * ──────────────────────────────────────────────────────
 * Read-only per-job lifecycle visual. Pure render of
 * `buildExecutionTimeline()`. No writers, no mutations.
 */

import { useMemo } from "react";
import { format } from "date-fns";
import {
  CheckCircle2, UserPlus, UserCog, CalendarClock, AlertOctagon,
  ShieldAlert, ShieldCheck, PauseCircle, PlayCircle, RotateCcw,
  Gavel, FileText, XCircle,
} from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { buildExecutionTimeline, type ExecutionTimelineEvent, type ExecutionTimelineEventKind } from "@/data/observability/executionTimeline";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const ICON_MAP: Record<ExecutionTimelineEventKind, React.ReactNode> = {
  review_approved: <CheckCircle2 className="h-3.5 w-3.5" />,
  installer_assigned: <UserPlus className="h-3.5 w-3.5" />,
  installer_changed: <UserCog className="h-3.5 w-3.5" />,
  install_scheduled: <CalendarClock className="h-3.5 w-3.5" />,
  callback_filed: <AlertOctagon className="h-3.5 w-3.5" />,
  holdback_created: <ShieldAlert className="h-3.5 w-3.5" />,
  holdback_released: <ShieldCheck className="h-3.5 w-3.5" />,
  holdback_paused: <PauseCircle className="h-3.5 w-3.5" />,
  holdback_resumed: <PlayCircle className="h-3.5 w-3.5" />,
  holdback_reversed: <RotateCcw className="h-3.5 w-3.5" />,
  dispute_opened: <Gavel className="h-3.5 w-3.5" />,
  dispute_evidence_added: <FileText className="h-3.5 w-3.5" />,
  dispute_withdrawn: <XCircle className="h-3.5 w-3.5" />,
  dispute_resolved: <CheckCircle2 className="h-3.5 w-3.5" />,
};

const TONE_MAP: Record<ExecutionTimelineEventKind, string> = {
  review_approved: "text-success bg-success/10",
  installer_assigned: "text-primary bg-primary/10",
  installer_changed: "text-warning bg-warning/10",
  install_scheduled: "text-primary bg-primary/10",
  callback_filed: "text-destructive bg-destructive/10",
  holdback_created: "text-warning bg-warning/10",
  holdback_released: "text-success bg-success/10",
  holdback_paused: "text-warning bg-warning/10",
  holdback_resumed: "text-primary bg-primary/10",
  holdback_reversed: "text-muted-foreground bg-muted",
  dispute_opened: "text-destructive bg-destructive/10",
  dispute_evidence_added: "text-muted-foreground bg-muted",
  dispute_withdrawn: "text-muted-foreground bg-muted",
  dispute_resolved: "text-success bg-success/10",
};

interface Props {
  jobId: string;
  /** When provided, scrolls in a contained card. */
  maxHeight?: number;
}

export function ExecutionTimeline({ jobId, maxHeight = 480 }: Props) {
  const { jobs, callbacks, holdbackLedger, disputeEvents } = useAppData();
  const job = jobs.find((j) => j.id === jobId);

  const events: ExecutionTimelineEvent[] = useMemo(() => {
    if (!job) return [];
    return buildExecutionTimeline({ job, callbacks, holdbackLedger, disputeEvents });
  }, [job, callbacks, holdbackLedger, disputeEvents]);

  if (!job) {
    return (
      <Card className="p-4 text-sm text-muted-foreground">
        Job not found.
      </Card>
    );
  }

  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Execution Timeline</h3>
          <p className="text-[11px] text-muted-foreground">
            Append-only lifecycle events for {job.id} • read-only
          </p>
        </div>
        <Badge variant="outline" className="text-[10px]">{events.length} events</Badge>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight }}>
        {events.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No lifecycle events yet for this job.
          </div>
        ) : (
          <ol className="p-3 space-y-1.5">
            {events.map((e) => (
              <li key={e.id} className="flex gap-2.5 rounded-md px-2 py-1.5 hover:bg-muted/40">
                <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${TONE_MAP[e.kind]}`}>
                  {ICON_MAP[e.kind]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] font-semibold">{e.label}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {format(new Date(e.at), "MMM d, yyyy h:mm a")}
                    </span>
                    {e.actorId && (
                      <span className="text-[10px] text-muted-foreground/70">• {e.actorId}</span>
                    )}
                  </div>
                  {e.detail && (
                    <p className="text-[11px] text-muted-foreground mt-0.5 whitespace-pre-wrap break-words">
                      {e.detail}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Card>
  );
}
